import {EventStatType, EventType} from "globals/types/models.ts";
import {useAppDispatch, useAppSelector} from "config/store";
import {useMount} from "react-use";
import {adminActions} from "config/store/modules/admin.ts";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import AdminConnectedContainer from "components/container/AdminConnectedContainer.tsx";
import {useTranslation} from "react-i18next";
import {tryAsyncFunc} from "globals/functions/async.ts";
import {EventFormValuesType, getValuesFromEvent} from "utils/form.ts";
import ajax from "config/ajax.ts";
import LoadingContainer from "components/container/LoadingContainer.tsx";
import {Form, Formik} from "formik";
import Button from "components/atom/button/Button.tsx";
import AdminEventForm from "components/layouts/form/AdminEventForm.tsx";
import EventStatsChart from "components/layouts/EventStatsChart.tsx";
import TabbedButtons from "components/layouts/TabbedButtons.tsx";

function AdminSingleEventPage() {
    const events: EventType[] = useAppSelector(state => state.admin.events)
    const {t} = useTranslation()
    const params = useParams()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()

    const [tab, setTab] = useState<'infos' | 'stats'>('infos')
    const [event, setEvent] = useState<EventType | null>(null)

    const [eventStats, setEventStats] = useState<EventStatType[]>([])
    //const [userEventStats, setUserEventStats] = useState([])

    useMount(async () => {
        if (!params.eventUuid) {
            navigate('/profile/admin/events')
            return
        }

        //get users necesary
        dispatch(adminActions.getUsers())

        const eventFromArr = events.find(e => e.uuid === params.eventUuid as string) || null

        if (eventFromArr) {
            setEvent(eventFromArr)
            return
        }

        const [eventFromReq, err] = await tryAsyncFunc(dispatch(adminActions.getEventByUuid(params.eventUuid as string)))

        if (err) {
            navigate('/profile/admin/events')
            return
        }

        setEvent(eventFromReq)
    })

    useEffect(() => {
        if (!event) return
        ajax.get(`/connected/admin/events/${event.id}/stats`)
            .then((res) => {
                setEventStats(res.data.eventStats)
            })
    }, [event])

    const deleteEvent = useCallback(async () => {
        if (!event) return

        if (!confirm(t('event.confirmDelete'))) {
            return
        }

        const [_, err] = await tryAsyncFunc(ajax.delete(`/connected/admin/events/id/${event.id}`))

        if (err) {
            return
        }

        await dispatch(adminActions.deletedEvent(event.id))
        navigate('/profile/admin/events')
    }, [event, t, dispatch, navigate])

    const detailEventStats = useMemo(() => {
        const data = {
            viewCount: 0,
            btnClickCount: 0
        }

        eventStats.forEach((eventStat: EventStatType) => {
            if (eventStat.type === 'view') {
                data.viewCount += eventStat.count
            }
            if (eventStat.type === 'btn_click') {
                data.btnClickCount += eventStat.count
            }
        })
        return data
    }, [eventStats])

    if (!event) {
        return null
    }


    return (
        <AdminConnectedContainer title={t('event.modify')}>
            <div className="w-full">
                <div className="mb-6">
                    <TabbedButtons
                        onClick={(tab) => setTab(tab as 'infos' | 'stats')}
                        selectedValue={tab}
                        buttons={[
                            {label: t('informations'), value: 'infos'},
                            {label: t('stats'), value: 'stats'}
                        ]}
                    />
                </div>
                <div className="w-full" style={{display: tab === 'stats' ? 'block' : 'none'}}>
                    <div>
                        <p>
                            {t('event.signaledCount')}: {event.signaledCount}</p>
                        <p>
                            {t('event.totalNumberOfViews')}: {detailEventStats.viewCount}
                        </p>
                        <p>
                            {t('event.totalNumberOfClicks')}: {detailEventStats.btnClickCount}
                        </p>

                        <EventStatsChart eventStats={eventStats}/>
                    </div>
                </div>
                <div className="w-full" style={{display: tab === 'infos' ? 'block' : 'none'}}>
                    <Formik
                        initialValues={getValuesFromEvent(event)}
                        validateOnChange={false}
                        validateOnBlur={false}
                        validate={values => {
                            const errors: Partial<{ [K in keyof EventFormValuesType]: string }> = {};//same keys as type but with string value
                            if (!values.titleFr) {
                                errors.titleFr = t('forms.errors.requiredTitleFr')
                            }
                            if (!values.categoryId) {
                                errors.categoryId = t('forms.errors.requiredCategory')
                            }
                            if (!values.dates || values.dates.length === 0) {
                                errors.dates = t('forms.errors.requiredDates')
                            }
                            if (!values.address) {
                                errors.address = t('forms.errors.requiredAddress')
                            }
                            return errors;
                        }}
                        onSubmit={async (values, {setSubmitting, setStatus, setFieldError, setValues}) => {
                            setSubmitting(true);
                            setStatus(null);

                            const [res, err] = await tryAsyncFunc(ajax.put(`/connected/admin/events/${event.id}`, values))
                            setSubmitting(false);

                            if (err) {
                                const errData = err.response.data || {}
                                const errors = errData.errors || {}

                                if (errors.global) {
                                    setStatus(errors.global)
                                    delete errors.global
                                }

                                Object.keys(errors).forEach(errKey => {
                                    setFieldError(errKey, errors[errKey])
                                })
                                return
                            }

                            setEvent(res.data.event)
                            setValues(getValuesFromEvent(res.data.event))

                            dispatch(adminActions.updatedEvent(res.data.event))
                        }}
                    >
                        {({isSubmitting, status}) => (
                            <LoadingContainer isLoading={isSubmitting} keepChildren>
                                <Form>
                                    {event?.signaledCount > 0 && (
                                        <p className="block text-red-500">
                                            {t('event.wasSignaled')}
                                        </p>
                                    )}
                                    {event?.deletedAt && (
                                        <p className="block text-red-500">
                                            {t('event.isDeleted')}
                                        </p>
                                    )}

                                    <AdminEventForm event={event}/>

                                    {status && (
                                        <p className="error block">{status}</p>
                                    )}
                                    <div className="mt-4 flex w-full flex-col items-center justify-center gap-4">
                                        <Button className="w-full" type="submit" disabled={isSubmitting}>
                                            {t('saveModifications')}
                                        </Button>
                                        <Button style="text-red" className="inline-block" type="button"
                                                disabled={isSubmitting}
                                                onClick={deleteEvent}>
                                            {t('event.delete')}
                                        </Button>
                                    </div>
                                </Form>
                            </LoadingContainer>
                        )}
                    </Formik>
                </div>

            </div>
        </AdminConnectedContainer>
    )
}

export default AdminSingleEventPage
